'use client';

import SectionWrapper from '@/components/_rebrand/craft/SectionWrapper';
import Image from '@/components/Image';
import { CUSTOMER_COUNT } from '@/constants/strings';
import { useModalButtonActions } from '@/hooks/useModalButtonActions';
import cn from 'classnames';
import { FC } from 'react';
import Link from '../../Link';
import HeroContent from './HeroContent';
import { backgroundColorMap } from './PageHero.constants';
import { PageHeroProps } from './PageHero.props';

const PageHero: FC<PageHeroProps> = ({
  eyebrow,
  title,
  description,
  links = [],
  featuredImage,
  logos = [],
  bullets,
  paragraph,
  textStyle = 'bullets',
  decorationImageUrl,
  backgroundColor = 'green',
  styleOverrides,
}) => {
  const { modalActionHandler } = useModalButtonActions();

  return (
    <SectionWrapper styleOverrides={styleOverrides} className="my-0">
      <div
        className={cn(
          backgroundColorMap[backgroundColor],
          'text-white pb-12 bg-right-top 3xl:bg-[length:45%_80%] bg-no-repeat md:bg-none'
        )}
        style={decorationImageUrl ? { backgroundImage: `url(${decorationImageUrl})` } : undefined}
      >
        <div className="-mt-header-height-mobile md:-mt-header-height-desktop pt-header-height-mobile md:pt-header-height-desktop">
          <div className="pt-11 md:pt-12 px-container-mobile md:px-container-desktop pb-10">
            <div className="mx-auto max-w-grid grid gap-2 md:gap-3 text-left">
              <div className="flex flex-col justify-center m-auto max-w-[520px] col-start-1 col-end-12 md:col-end-6 ">
                <div className="text-micro1 mb-[12px]">{eyebrow}</div>
                <h1 className={cn("font-500 tracking-tight text-h1 md:text-display3 mb-2",
                  description ? 'mb-2' : 'mb-4')
                }>{title}</h1>
                {description && <p className="text-white text-p1 max-w-80 text-pretty mb-4">{description}</p>}

                <HeroContent textStyle={textStyle} paragraph={paragraph} bullets={bullets} />
                <ul className="flex gap-1 md:gap-2">
                  {links.map((link, index) => (
                    <li key={`product-hero-link-${link.title?.replace(/\s+/g, '-') ?? index}`}>
                      {link.href.length ? (
                        <Link
                          className={cn('block button button-size-medium', {
                            'button-primary-white': link.type === 'primary',
                            'button-secondary-citron-300': link.type === 'secondary',
                          })}
                          href={link.href}
                        >
                          {link.title}
                        </Link>
                      ) : (
                        <button
                          className={cn('block button button-size-medium', {
                            'button-primary-citron': link.type === 'primary',
                            'button-secondary-citron-300': link.type === 'secondary',
                          })}
                          onClick={() => {
                            modalActionHandler(link.action);
                          }}
                        >
                          {link.title}
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex w-full max-w-[620px] col-start-1 col-end-12 md:col-start-7 justify-end relative z-10 mt-12 md:mt-0">
                <div className="relative w-full h-full">

                  <Image
                    src={featuredImage.desktop.url}
                    width={featuredImage.desktop.width}
                    height={featuredImage.desktop.height}
                    alt={featuredImage.desktop.alt}
                  />
                </div>
              </div>

              <div className="hidden md:flex col-start-1 col-end-12 w-full items-center justify-between mt-7">
                <div className="flex items-center">
                  <div className="flex flex-wrap text-violet-200 text-caption1">
                    <span className="font-500 text-p2 mr-[6px]">Trusted by {CUSTOMER_COUNT}</span>
                    <span className="text-p2">companies worldwide</span>
                  </div>
                </div>
                <div className="flex items-center">
                  {logos.map((logo, index) => (
                    <div key={`page-hero-logo-${index}`} className="flex ml-[20px] 2xl:ml-[70px]">
                      {logo}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
};

export default PageHero;
